import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, Badge } from "@/components/ui/data";
import { Button } from "@/components/ui/button";
import { Select } from "@/components/ui/form";
import { api } from "@/lib/api";
import { formatDateTime } from "@/lib/utils";
import { useToast } from "@/app/Toast";

interface BookingRow {
  id: number;
  code: string;
  status: string;
  startAt: string;
   customer: { name: string; phone: string | null };
   vehicle: { id: number; plate: string; make: string | null; model: string | null; color: string | null } | null;
  resource: { name: string };
  resources: Array<{ id: number; name: string }>;
  version: number;
}

interface LocationRow { id: number; name: string; timezone: string; active: boolean; }

const NEXT: Record<string, string> = {
  CONFIRMED: "CHECKED_IN",
  CHECKED_IN: "IN_PROGRESS",
  IN_PROGRESS: "COMPLETED",
};

export function QueuePage() {
  const { toast } = useToast();
  const [rows, setRows] = useState<BookingRow[]>([]);
  const [locations, setLocations] = useState<LocationRow[]>([]);
  const [locationId, setLocationId] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const location = locations.find((item) => item.id === locationId);

  async function refresh() {
    if (!location) return;
    setLoading(true);
    setError(null);
    const today = dateInZone(new Date(), location.timezone);
    const tomorrow = addDay(today);
    try {
      const r = await api.get<BookingRow[]>(`/api/v1/bookings?locationId=${location.id}&from=${today}&to=${tomorrow}&limit=200`);
      setRows(r);
    } catch (error) {
      const message = error instanceof Error ? error.message : "Could not load today's queue";
      setError(message);
      toast("error", message);
    } finally { setLoading(false); }
  }

  useEffect(() => {
    api.get<LocationRow[]>("/api/v1/locations").then((items) => {
      const active = items.filter((item) => item.active);
      setLocations(active);
      setLocationId(active[0]?.id ?? null);
    }).catch((error) => toast("error", error instanceof Error ? error.message : "Could not load locations"));
  }, []);

  useEffect(() => { void refresh(); }, [locationId]);

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div><h1 className="text-2xl font-semibold tracking-tight">Today's queue</h1>{location && <p className="text-sm text-muted-foreground">{location.name} · {location.timezone}</p>}</div>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
          <Select className="w-full sm:w-auto" value={locationId?.toString() ?? ""} onChange={(event) => setLocationId(Number(event.target.value))}>
            {locations.map((item) => <option key={item.id} value={item.id}>{item.name}</option>)}
          </Select>
          <Button onClick={() => void refresh()} variant="outline" disabled={loading}><span className={loading ? "animate-spin" : ""}>↻</span> {loading ? "Loading" : "Refresh"}</Button>
        </div>
      </div>
      {error && <div role="alert" className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-3 text-sm text-destructive"><span>{error}</span><Button size="sm" variant="outline" onClick={() => void refresh()}>Try again</Button></div>}
      <Card>
        <CardHeader><CardTitle className="text-base">Now & next</CardTitle></CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
          <Table className="min-w-[760px]">
            <TableHeader>
              <TableRow>
                <TableHead>Time</TableHead>
                <TableHead>Code</TableHead>
                <TableHead>Customer</TableHead>
                 <TableHead>Vehicle</TableHead>
                 <TableHead>Bay</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((b) => (
                <TableRow key={b.id}>
                  <TableCell>{formatDateTime(b.startAt, location?.timezone)}</TableCell>
                  <TableCell className="font-mono">
                    <Link to={`/bookings/${b.id}`} className="hover:underline">{b.code}</Link>
                  </TableCell>
                  <TableCell>{b.customer.name}</TableCell>
                   <TableCell>{b.vehicle ? <><span className="font-mono">{b.vehicle.plate}</span><span className="ml-2 text-xs text-muted-foreground">{[b.vehicle.make, b.vehicle.model].filter(Boolean).join(" ")}</span></> : <span className="text-muted-foreground">—</span>}</TableCell>
                   <TableCell>{(b.resources.length ? b.resources : [b.resource]).map((resource) => resource.name).join(", ")}</TableCell>
                  <TableCell><Badge>{b.status}</Badge></TableCell>
                   <TableCell className="whitespace-nowrap text-right">
                    <div className="flex justify-end gap-2">
                      {NEXT[b.status] && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={async () => {
                            try {
                              await api.post(`/api/v1/bookings/${b.id}/status`, { status: NEXT[b.status], version: b.version });
                              await refresh();
                            } catch (e) { toast("error", e instanceof Error ? e.message : "Status update failed"); }
                          }}
                        >
                          → {NEXT[b.status]}
                        </Button>
                      )}
                      {(b.status === "CONFIRMED" || b.status === "CHECKED_IN") && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={async () => {
                            if (!confirm("Mark as no-show?")) return;
                            try {
                              await api.post(`/api/v1/bookings/${b.id}/status`, { status: "NO_SHOW", version: b.version });
                              await refresh();
                            } catch (e) { toast("error", e instanceof Error ? e.message : "Status update failed"); }
                          }}
                        >
                          No-show
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {loading && (
                 <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground">Loading today's queue…</TableCell></TableRow>
              )}
              {!loading && rows.length === 0 && (
                 <TableRow><TableCell colSpan={7} className="text-center text-muted-foreground">No bookings today.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

function dateInZone(date: Date, timeZone: string): string {
  const parts = new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(date);
  const get = (type: string) => parts.find((part) => part.type === type)?.value ?? "";
  return `${get("year")}-${get("month")}-${get("day")}`;
}

function addDay(date: string): string {
  const value = new Date(`${date}T00:00:00Z`);
  value.setUTCDate(value.getUTCDate() + 1);
  return value.toISOString().slice(0, 10);
}
